/**
 * Create-report panel for reports.
 */
import * as React from 'react';
import { Star, FileText, AlertCircle } from 'lucide-react';
import { Tabs, type TabItem } from '@/components/ui/tabs';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { Input } from '@/components/forms/Input';
import {
  REPORT_REMOTE_SCHEMA,
  REPORT_REMOTE_SCORING_SCHEMA,
} from '@/components/reports/report-configs';
import type { ReportTab as Tab } from '@/types/remote/report.types';
import { ReportTab } from '@/components/reports/remote/report-form-builder/ReportTab';
import { ScoringTab } from '@/components/reports/remote/report-form-builder/ScoringTab';
import {
  applyReportFieldPatch,
  resolveSchemaId,
} from '@/components/reports/remote/report-form-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Props for {@link CreateReportView}. */
interface CreateReportViewProps {
  sessionType: Tab;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Create panel for a remote report. */
export function CreateReportView({
  sessionType,
  onClose,
  onSuccess,
}: Readonly<CreateReportViewProps>) {
  const [activeTab, setActiveTab] = React.useState<'report' | 'scoring'>('report');
  const [draftFields, setDraftFields] = React.useState<Record<string, unknown>>({});

  const tabs: TabItem<'report' | 'scoring'>[] = [
    { id: 'report', label: 'Compte rendu', icon: <FileText className="w-3 h-3" /> },
    { id: 'scoring', label: 'Scoring', icon: <Star className="w-3 h-3" /> },
  ];

  const schemaId = resolveSchemaId(sessionType);

  let createTitle = 'Créer un compte rendu';
  let createSubtitle = 'Renseignez les informations du compte rendu';
  if (schemaId === REPORT_REMOTE_SCHEMA.tutoring) {
    createTitle = 'Créer un compte rendu de tutorat individuel';
    createSubtitle = 'Renseignez les informations du compte rendu de tutorat individuel';
  } else if (schemaId === REPORT_REMOTE_SCHEMA.synthesisInformationMeeting) {
    createTitle = "Créer une synthèse de réunion d'information";
    createSubtitle = "Renseignez les informations de la synthèse de réunion d'information";
  } else if (schemaId === REPORT_REMOTE_SCHEMA.synthesisWelcomeMeeting) {
    createTitle = "Créer une synthèse de réunion d'accueil";
    createSubtitle = "Renseignez les informations de la synthèse de réunion d'accueil";
  }

  const applyPatch = (patch: Record<string, unknown>) =>
    setDraftFields((prev) => applyReportFieldPatch(prev, patch));

  /** Validate, POST the report, then close. */
  const handleSubmit = () => {
    toast.info(getErrorMessage('remoteReportManualCreateUnavailable'));
    onSuccess?.();
    onClose();
  };

  return (
    <CollapseReplaceForm
      formType="create"
      title={createTitle}
      subtitle={createSubtitle}
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Créer"
      isSubmitting={false}
      isValid={activeTab === 'report'}
    >
      <div className="rounded-lg border border-border bg-muted/30 p-4 flex gap-3 mb-4">
        <AlertCircle className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
        <div className="text-sm text-foreground">
          <p className="font-medium mb-1">Création automatique</p>
          <p className="text-muted-foreground">
            Les comptes rendus sont créés automatiquement par le système lorsqu'une séance démarre.
            La création manuelle est destinée aux cas d'exception uniquement.
          </p>
        </div>
      </div>

      <Input
        label="Séance"
        placeholder="Rechercher une séance..."
        inputSize="sm"
        helperText="Sélectionnez une séance sans CR existant"
        disabled
        className="mb-4"
      />

      <Tabs
        items={tabs}
        activeTab={activeTab}
        onTabChange={(tab) => setActiveTab(tab as 'report' | 'scoring')}
        className="mb-4"
      />

      {activeTab === 'report' && (
        <ReportTab
          schemaId={schemaId}
          status="a_saisir"
          fields={draftFields}
          onChange={applyPatch}
          isReadOnly={false}
        />
      )}

      {activeTab === 'scoring' && (
        <ScoringTab schemaId={REPORT_REMOTE_SCORING_SCHEMA.student} entries={[]} />
      )}
    </CollapseReplaceForm>
  );
}
